import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { uniqueNamesGenerator, Config, colors, adjectives, animals } from 'unique-names-generator';
import { useUser } from '../../contexts/UserContext';

interface CreateCounterModalProps {
  isCreating: boolean;
  onCreateCounter: (name: string, isPrivate?: boolean, email?: string) => Promise<void>;
  onModalHidden?: () => void;
}

const CreateCounterModal: React.FC<CreateCounterModalProps> = ({
  isCreating,
  onCreateCounter,
  onModalHidden
}) => {
  const { t } = useTranslation();
  const { isLoggedIn, user } = useUser();
  const navigate = useNavigate();
  
  const [newCounterName, setNewCounterName] = useState('');
  // Pre-tick the private checkbox for logged-in users
  const [isPrivate, setIsPrivate] = useState(isLoggedIn);
  const [email, setEmail] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const modalInstance = useRef<Modal | null>(null);

  useEffect(() => {
    if (modalRef.current) {
      modalInstance.current = new Modal(modalRef.current);
      
      // Generate a random name when modal shows
      modalRef.current.addEventListener('show.bs.modal', generateRandomName);
      
      // Reset form when modal is hidden
      modalRef.current.addEventListener('hidden.bs.modal', () => {
        resetForm();
        if (onModalHidden) {
          onModalHidden();
        }
      });
    }
    
    // Cleanup event listeners
    return () => {
      if (modalRef.current) {
        modalRef.current.removeEventListener('show.bs.modal', generateRandomName);
        modalRef.current.removeEventListener('hidden.bs.modal', resetForm);
      }
    };
  }, [onModalHidden]);

  const generateRandomName = () => {
    // Randomly choose between two patterns
    const useAlternatePattern = Math.random() > 0.5;
    
    const nameConfig1: Config = {
      dictionaries: [colors, adjectives, animals],
      separator: '-',
      style: 'capital'
    };
    
    const nameConfig2: Config = {
      dictionaries: [adjectives, colors, animals],
      separator: '-',
      style: 'capital'
    };
    
    const randomName = uniqueNamesGenerator(useAlternatePattern ? nameConfig2 : nameConfig1);
    setNewCounterName(randomName);
  };

  const resetForm = () => {
    setNewCounterName('');
    // Reset isPrivate based on login status
    setIsPrivate(isLoggedIn);
    setEmail('');
    setCreateError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCounterName.trim()) {
      setCreateError(t('errors.required', 'Counter name is required'));
      return;
    }

    try {
      setCreateError(null);
      // For logged-in users, use their email automatically
      const emailToUse = isLoggedIn ? user.email || '' : email;
      await onCreateCounter(newCounterName, isPrivate, emailToUse);
      
      // Modal will be hidden by the parent component
    } catch (err) {
      setCreateError(t('errors.failedToCreate', 'Failed to create counter'));
      console.error(err);
    }
  };

  return (
    <div 
      className="modal fade" 
      id="createCounterModal" 
      tabIndex={-1} 
      aria-labelledby="createCounterModalTitle" 
      aria-hidden="true"
      ref={modalRef}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fs-6" id="createCounterModalTitle">
              {t('counters.createNew', 'Create New Counter')}
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              data-bs-dismiss="modal" 
              aria-label={t('common.close', 'Close')}
            ></button>
          </div>
          <div className="modal-body text-start">
            {createError && (
              <div className="alert alert-danger py-2 px-3" role="alert">
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <div className="small">{createError}</div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="counterName" className="form-label small">
                  {t('counters.name', 'Counter Name')}
                </label>
                <div className="input-group input-group-sm">
                  <input
                    type="text"
                    className="form-control"
                    id="counterName"
                    value={newCounterName}
                    onChange={(e) => setNewCounterName(e.target.value)}
                    onClick={(e) => e.currentTarget.select()}
                    onFocus={(e) => e.currentTarget.select()}
                    required
                    aria-required="true"
                    placeholder={t('counters.namePlaceholder', 'Enter a name for your counter')}
                  />
                  <span className="input-group-text">
                    <i className="bi bi-pencil-square" aria-hidden="true"></i>
                  </span>
                </div>
                <div className="form-text small">
                  <i className="bi bi-info-circle-fill me-1" aria-hidden="true"></i>
                  {t('counters.suggestionHint', 'A suggested name is provided, feel free to customize it')}
                </div>
              </div>
              
              {/* Only show email field for non-logged-in users */}
              {!isLoggedIn && (
                <div className="mb-3">
                  <label htmlFor="counterEmail" className="form-label small">
                    {t('counters.emailField', 'Email Address')}
                    <span className="text-secondary ms-1">({t('common.optional', 'Optional')})</span>
                  </label>
                  <div className="input-group input-group-sm">
                    <input
                      type="email"
                      className="form-control"
                      id="counterEmail"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('counters.emailPlaceholder', 'Receive counter links via email')}
                      aria-required="false"
                    />
                    <span className="input-group-text">
                      <i className="bi bi-envelope" aria-hidden="true"></i>
                    </span>
                  </div>
                  <div className="form-text small">
                    <i className="bi bi-info-circle-fill me-1" aria-hidden="true"></i>
                    {t('counters.emailHint', 'If provided, we will send you links to access this counter')}
                  </div>
                  <div className="mt-2 border-top pt-2 text-center">
                    <small className="text-body-secondary">
                      Want to manage all your counters in one place?
                    </small>
                    <div className="mt-1">
                      <button 
                        type="button" 
                        className="btn btn-link btn-sm text-decoration-none p-0 me-2" 
                        data-bs-dismiss="modal"
                        onClick={() => navigate('/signup')}
                      >
                        <i className="bi bi-person-plus me-1"></i>
                        Sign up
                      </button>
                      <span className="text-body-secondary">or</span>
                      <button 
                        type="button" 
                        className="btn btn-link btn-sm text-decoration-none p-0 ms-2" 
                        data-bs-dismiss="modal"
                        onClick={() => navigate('/login')}
                      >
                        <i className="bi bi-box-arrow-in-right me-1"></i>
                        Log in
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="privateCounter"
                    checked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="privateCounter">
                    {t('counters.privateCounter', 'Private counter')}
                  </label>
                </div>
                <div className="form-text small">
                  <i className="bi bi-info-circle-fill me-1" aria-hidden="true"></i>
                  {isLoggedIn 
                    ? 'Private counters will only be visible to you in your counter list and won\'t appear in public listings.'
                    : t('counters.privateCounterHint', 'Private counters are only visible to admin users in the counters list')}
                </div>
              </div>
              
              <div className="modal-footer px-0 pb-0 justify-content-end">
                <div className="d-flex justify-content-end gap-2 w-100">
                  <button 
                    type="button" 
                    className="btn btn-secondary btn-sm"
                    data-bs-dismiss="modal"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-sm"
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('common.creating', 'Creating...')}
                      </>
                    ) : (
                      t('counters.create', 'Create Counter')
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCounterModal; 